import React, { useRef, useState } from 'react';
import { filter } from 'lodash'
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import personFill from '@iconify/icons-eva/person-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import { useNavigate } from 'react-router-dom';
// material
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, Typography } from '@mui/material';
// components
import MIconButton from '../components/@material-extend/MIconButton';
import MyAvatar from '../components/MyAvatar';
import MenuPopover from '../components/MenuPopover';
import { AppState } from '../Common/Reducer/rootReducerTypes';
import { BackendTypeName } from "../Common/Utils/backendDataParser";
import useBackendData from '../hooks/useBackendData';
import useLocales from '../hooks/useLocales';
import { buildHttpHeaders } from '../utils/buildHttpHeaders';
import { useDisplayName } from '../hooks/useDisplayName';
import { getAppSettings } from '../Common/Utils/localStorageHelper';
import { AuthMethod } from '../Common/enums';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const user = useSelector((state: AppState) => state.user);
  const displayName = useDisplayName();
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const { translate } = useLocales();
  const [backToOldUrl, setBackToOldUrl] = React.useState('');
  const [selectedClient] = useBackendData();
  const authMethod = getAppSettings().authMethod;

  // ----------------------------------------------------------------------

  let MENU_OPTIONS = selectedClient && selectedClient.backendName === BackendTypeName.Handyman ?
    [{
      id: 'profile',
      label: `${translate("accountPopover." + "Profile")}`,
      icon: personFill,
      linkTo: 'profile'
    },
    {
      id: 'change-password',
      label: `${translate("accountPopover." + "ChangePassword")}`,
      icon: settings2Fill,
      linkTo: 'change-password'
    }
    ] :
    [
      {
        id: 'profile',
        label: `${translate("accountPopover." + "Profile")}`,
        icon: personFill,
        linkTo: 'profile'
      },
      {
        id: 'change-password',
        label: `${translate("accountPopover." + "ChangePassword")}`,
        icon: settings2Fill,
        linkTo: 'change-password'
      }
    ]
    ;

  React.useEffect(() => {
    if (user) {

      fetch('settings/hmweburl', {
        headers: buildHttpHeaders(selectedClient.name)
      })
        .then(response => response.text())
        .then((data) => {
          setBackToOldUrl(data.toString());
        });
    }
  }, [user]);

  let email = '';

  if (user && user.profile) {
    email = authMethod === AuthMethod.AzureB2C ? user.profile.email! : user.profile.name!;
  }

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const switchToOld = () => {
    // eslint-disable-next-line no-restricted-globals
    location.assign(`${backToOldUrl}?useGsGroupLogin=true`);
  };

  const handleNavigate = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    const selectedOption = filter(MENU_OPTIONS, (option) => option.id === event.currentTarget.id)[0];
    if (selectedOption.id === 'switchToOld') {
      switchToOld();
    } else {
      window.open(`/${selectedOption.linkTo}`, "_blank");
    }
    handleClose();
  };

  const handleLogout = async () => {
    try {
      navigate("/logout");
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        style={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              backgroundColor: alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar color="primary" />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        style={{ width: 220 }}
      >
        <Box style={{
          marginTop: theme.spacing(1.5),
          marginBottom: theme.spacing(1.5),
          paddingLeft: theme.spacing(2.5),
          paddingRight: theme.spacing(2.5),
        }}>
          <Typography variant="subtitle1" noWrap>
            {displayName}
          </Typography>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary }} noWrap>
            {email}
          </Typography>
        </Box>

        <Divider style={{
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }} />

        {authMethod === AuthMethod.IDM && MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            id={option.id}
            onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>) => handleNavigate(event)}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24
              }}
            />
            <Icon icon={option.icon} style={{ marginRight: theme.spacing(0.5) }} />
            {option.label}
          </MenuItem>
        ))}

        <Box style={{
          padding: theme.spacing(2),
          paddingTop: theme.spacing(1.5)
        }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            {translate("accountPopover." + "Logout")}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
